import { Link, Outlet, useRouterState } from '@tanstack/react-router';

import { AdminTopbar } from './admin-topbar';
import { Toaster } from '../components/ui';
import { useAdminAuth } from '../lib/auth-context';
import { usePendingWork, type PendingWork } from '../lib/use-pending-work';
import { SignInPage } from '../pages/sign-in-page';

interface NavItem {
  to:
    | '/'
    | '/orders'
    | '/refunds'
    | '/payments'
    | '/deposit-returns'
    | '/proposals'
    | '/sales'
    | '/chats'
    | '/listings'
    | '/sponsorships'
    | '/sponsorship-applications'
    | '/settlements'
    | '/royalties'
    | '/policies'
    | '/users'
    | '/sns-requests'
    | '/reports'
    | '/support'
    | '/community'
    | '/admins'
    | '/audit-logs'
    | '/settings/payment'
    | '/settings/site'
    | '/settings/community'
    | '/settings/sponsorship'
    | '/settings/listing'
    | '/settings/company'
    | '/settings/social'
    | '/settings/scheduler'
    | '/settings/security'
    | '/app-versions';
  label: string;
  exact?: boolean;
  /** 처리할 일이 있으면 메뉴 오른쪽에 빨간 점 */
  attention?: PendingWork;
}

/** LNB 구역 — title null 이면 구역 제목 없이 나열 (대시보드) */
const NAV_SECTIONS: { title: string | null; items: NavItem[] }[] = [
  { title: null, items: [{ to: '/', label: '대시보드', exact: true }] },
  {
    title: '운영',
    items: [
      { to: '/orders', label: '주문', attention: 'orders' },
      { to: '/refunds', label: '환불', attention: 'refunds' },
      { to: '/payments', label: '입금 관리', attention: 'payments' },
      { to: '/deposit-returns', label: '입금 반환', attention: 'depositReturns' },
      { to: '/proposals', label: '조건 협의' },
      { to: '/chats', label: '채팅 관리' },
      { to: '/listings', label: '리스팅', attention: 'listings' },
      { to: '/sponsorships', label: '협찬 심사', attention: 'sponsorships' },
      { to: '/sponsorship-applications', label: '협찬 지원 내역' },
      { to: '/sales', label: '매출' },
      { to: '/settlements', label: '정산', attention: 'settlements' },
      { to: '/royalties', label: '로열티', attention: 'royalties' },
    ],
  },
  {
    title: '회원',
    items: [
      { to: '/users', label: '회원' },
      { to: '/sns-requests', label: '인플루언서 인증 요청', attention: 'snsRequests' },
      { to: '/reports', label: '신고', attention: 'reports' },
      { to: '/support', label: '1:1 문의', attention: 'support' },
      { to: '/community', label: '커뮤니티 글' },
    ],
  },
  {
    title: '플랫폼 세팅',
    items: [
      { to: '/settings/company', label: '회사 정보' },
      { to: '/settings/site', label: '홈페이지 설정' },
      { to: '/settings/social', label: '소셜 링크' },
      { to: '/settings/payment', label: '결제 설정' },
      { to: '/settings/community', label: '커뮤니티 설정' },
      { to: '/settings/sponsorship', label: '협찬 설정' },
      { to: '/settings/listing', label: 'IP 상품 설정' },
      { to: '/policies', label: '약관 관리' },
      { to: '/app-versions', label: '앱 버전 관리' },
      { to: '/settings/scheduler', label: '스케줄러 관리' },
      { to: '/admins', label: '관리자 계정' },
      { to: '/settings/security', label: '보안 설정' },
      { to: '/audit-logs', label: '감사 로그' },
    ],
  },
];

/** 사이드바 + 인증 게이트. 모든 어드민 페이지의 공통 셸. */
export function AdminShell() {
  const { session, ready, signOut } = useAdminAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const attention = usePendingWork(session?.accessToken, pathname);

  // 새로고침 직후엔 httpOnly 쿠키로 세션 복원 중 — 끝나기 전 로그인 화면을 그리면 깜빡인다
  if (!ready) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        세션 확인 중…
      </div>
    );
  }
  if (!session) return <SignInPage />;

  return (
    // 화면 높이에 고정하고 사이드바·본문을 각자 스크롤한다 — 긴 목록을 내려도 메뉴가 그대로 보인다
    <div className="flex h-screen overflow-hidden">
      <aside className="flex w-56 shrink-0 flex-col border-r border-border bg-background p-4">
        <div className="mb-4 shrink-0 px-2 text-lg font-extrabold tracking-tight">
          Inflip <span className="text-xs font-semibold text-muted-foreground">ADMIN</span>
        </div>
        {/* 메뉴만 스크롤 — 로고와 로그아웃은 위아래에 고정 */}
        <nav className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-1 pb-4">
          {NAV_SECTIONS.map((section) => (
            <div key={section.title ?? 'root'} className="flex flex-col gap-0.5">
              {section.title && (
                <p className="px-3 pb-1.5 pt-5 text-[11px] font-bold text-muted-foreground/80">
                  {section.title}
                </p>
              )}
              {section.items.map((item) => {
                const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={
                      active
                        ? 'rounded-md bg-brand-soft px-3 py-2.5 text-sm font-bold text-brand'
                        : 'rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground'
                    }
                  >
                    <span className="flex items-center justify-between gap-2">
                      {item.label}
                      {item.attention && attention[item.attention] && (
                        <span
                          role="status"
                          aria-label="처리 대기 중인 건 있음"
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500"
                        />
                      )}
                    </span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <button
          type="button"
          onClick={signOut}
          className="shrink-0 rounded-md bg-muted px-3 py-2.5 text-sm font-semibold text-muted-foreground transition hover:bg-border/70 hover:text-foreground"
        >
          로그아웃
        </button>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-muted/40">
        <AdminTopbar />
        {/* 본문 스크롤 영역 — 페이지 이동 시 맨 위로 (router 의 scrollToTopSelectors) */}
        <div id="admin-content" className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </div>
      <Toaster />
    </div>
  );
}
